import { Box, Stack, Text, useMantineTheme } from '@mantine/core';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { DashboardMetric } from '../../../types/dashboard';
import { CardShell } from '../atlas/cards/CardShell';
import { MetricRow } from '../atlas/cards/MetricRow';
import {
  aggregateQuarterlyChartToAnnual,
  attachGoalValuesToChartData,
  buildChartYDomain,
  collectGoalValues,
  extractValueKeys,
  formatMetricTitle,
  getAllTimeValue,
  getMetricSeries,
  getPointValue,
  humanizeFieldName,
  latest,
  parseQuarter,
  pctChange,
  previous,
} from './componentProjectMetricUtils';
import { renderChartGoalLines } from '../ChartGoalLines';

const CHART_COLORS = ['#339AF0', '#51CF66', '#FAB005', '#FF6B6B', '#845EF7', '#22B8CF'];

type ComponentProjectMetricChartCardProps = {
  metric: DashboardMetric;
  sectionTitle?: string;
};

export function ComponentProjectMetricChartCard({
  metric,
  sectionTitle,
}: ComponentProjectMetricChartCardProps) {
  const theme = useMantineTheme();
  const series = getMetricSeries(metric);
  const valueKeys = extractValueKeys(series);
  const title = formatMetricTitle(metric.metricId, valueKeys);
  const subtitle = sectionTitle
    ? `${sectionTitle} · ${String(latest(series)?.quarter ?? 'No data')}`
    : String(latest(series)?.quarter ?? 'No data');

  const cur = latest(series);
  const prev = previous(series);
  const curValue = cur ? getPointValue(cur, valueKeys) : null;
  const prevValue = prev ? getPointValue(prev, valueKeys) : null;
  const qoq = curValue !== null ? pctChange(curValue, prevValue) : null;
  const arrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');
  const qoqText =
    qoq === null ? '—' : arrow(qoq) ? `${arrow(qoq)}${Math.abs(qoq).toFixed(2)}% QoQ` : '0.00% QoQ';

  const currentMeta = parseQuarter(String(cur?.quarter ?? ''));
  const ytd = currentMeta
    ? series
        .filter((point) => {
          const meta = parseQuarter(String(point.quarter ?? ''));
          return meta?.year === currentMeta.year && meta.quarter <= currentMeta.quarter;
        })
        .reduce((sum, point) => sum + getPointValue(point, valueKeys), 0)
    : 0;

  const yoy = (() => {
    if (!currentMeta) return null;
    const prevYearTotal = series
      .filter((point) => {
        const meta = parseQuarter(String(point.quarter ?? ''));
        return meta?.year === currentMeta.year - 1 && meta.quarter <= currentMeta.quarter;
      })
      .reduce((sum, point) => sum + getPointValue(point, valueKeys), 0);
    return pctChange(ytd, prevYearTotal);
  })();
  const yoyText =
    yoy === null ? '— YoY' : yoy === 0 ? '0.00% YoY' : `${arrow(yoy)}${Math.abs(yoy).toFixed(2)}% YoY`;

  const allTime = getAllTimeValue(metric.allTime, valueKeys);
  const unitsLabel = metric.units && metric.units !== 'count' ? ` ${metric.units}` : '';
  const latestQuarter = String(cur?.quarter ?? '—');

  const quarterlyChartData = attachGoalValuesToChartData(
    series.map((point) => {
      const row: Record<string, string | number> = {
        quarter: String(point.quarter ?? ''),
      };
      for (const key of valueKeys) {
        row[key] = getPointValue({ [key]: point[key] }, [key]);
      }
      return row;
    }),
    series,
    valueKeys
  );

  const chartData = aggregateQuarterlyChartToAnnual(quarterlyChartData, valueKeys);

  const goalValues = collectGoalValues(chartData, valueKeys);
  const yDomain = buildChartYDomain(
    chartData.flatMap((point) => valueKeys.map((key) => Number(point[key] ?? 0))),
    goalValues
  );
  const hasGoals = goalValues.length > 0;
  const ChartComponent = hasGoals ? ComposedChart : BarChart;

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  if (valueKeys.length === 0 || series.length === 0) {
    return (
      <CardShell title={title} subtitle="No data">
        <Text size="sm" color="dimmed">
          No quarterly data available for this metric yet.
        </Text>
      </CardShell>
    );
  }

  return (
    <CardShell title={title} subtitle={subtitle}>
      <Stack spacing="sm">
        <MetricRow
          items={[
            {
              label: 'This Quarter',
              labelTooltip: `${title} reported for ${latestQuarter}. Quarter-over-quarter change compares to the prior reported quarter.`,
              value: (
                <Text size="lg" weight={800}>
                  {curValue ?? '—'}
                </Text>
              ),
              sublabel: qoqText,
            },
            {
              label: 'YTD',
              labelTooltip: `${title} calendar year to date total (sum of quarters in the current calendar year through ${latestQuarter}).`,
              value: (
                <Text size="lg" weight={800}>
                  {ytd}
                </Text>
              ),
              sublabel: yoyText,
            },
            {
              label: 'All-Time',
              labelTooltip: `${title} across all reported quarters (includes periods not shown on the chart).`,
              value: (
                <Text size="lg" weight={800}>
                  {allTime}
                </Text>
              ),
            },
          ]}
        />

        <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginTop: '0.75rem' }}>
          Annual Trend
        </Text>

        <Box sx={{ height: 170 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ChartComponent data={chartData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.18} vertical={false} />
              <XAxis
                dataKey="yearLabel"
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
                minTickGap={12}
              />
              <YAxis
                orientation="right"
                tick={{ fontSize: 11 }}
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                width={44}
                tickMargin={8}
                domain={yDomain}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  return (
                    <div style={tooltipShellStyle}>
                      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>
                        {label}
                      </div>
                      {payload.map((entry) => (
                        <div
                          key={String(entry.dataKey)}
                          style={{ fontWeight: 700, fontSize: 13, lineHeight: 1.4 }}
                        >
                          {humanizeFieldName(String(entry.dataKey))}: {entry.value}
                          {unitsLabel}
                        </div>
                      ))}
                    </div>
                  );
                }}
              />
              {valueKeys.length > 1 ? (
                <Legend
                  verticalAlign="top"
                  height={28}
                  formatter={(value) => humanizeFieldName(String(value))}
                  wrapperStyle={{ fontSize: 11 }}
                />
              ) : null}
              {hasGoals ? renderChartGoalLines(chartData, valueKeys) : null}
              {valueKeys.map((key, index) => (
                <Bar
                  key={key}
                  dataKey={key}
                  fill={CHART_COLORS[index % CHART_COLORS.length]}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={valueKeys.length > 1 ? 28 : 40}
                />
              ))}
            </ChartComponent>
          </ResponsiveContainer>
        </Box>
      </Stack>
    </CardShell>
  );
}
